/**
 * `import` 契约 —— **D 域账单导入**（`DR-051` · 批 86）
 *
 * 依据：`D域PRD` **§5.1**（解析）· **§5.2**（去重）· **§5.3**（规则）· **§5.4**（归因）·
 * `AC-D1.1`–`D1.13` · `AC-D2.1`–`D2.10` · `AC-D3.5`–`D3.8` · `AC-D5.1`/`D5.2` · `DR-051` D1–D9
 *
 * ## 🔴 两段式：**预览不落库，落库用 `previewId`**
 *
 * 原文件**不存**（`AC-D5.5`）：`preview` 把解析结果留在服务端内存（TTL 10 分钟），
 * 返回一个 `previewId`；确认时只发**决策**（`gridRow → 分类`），由服务端**重新取回解析结果**落库。
 *
 * 🔴 **为什么不把整批行回传**：① 1000 行 JSON 会超过默认 100 KB 请求体；
 * ② 更重要 —— 回传就等于**信任端侧的金额与日期**，那是把"解析口径"变成两份实现（铁律 24）。
 */

import { z } from 'zod';
import { ImportPolicy } from '../constants/index.js';
import type { Cents } from '../types/brand.js';

/** 编码（与 `nucleus/import.ts` 的 `ImportEncoding` 同值） */
export const ImportEncodingSchema = z.enum(['utf-8', 'gbk', 'gb18030']);

/**
 * `POST /api/imports/preview` —— 查询参数（**请求体是原始 CSV 字节，不是 JSON**）。
 *
 * 🔴 `accountId` 必填：一次导入绑定一个账户（`§5.1.6`），逐行可改由后续批次做。
 */
export const ImportPreviewQuerySchema = z.object({
  accountId: z.string().trim().min(1, '请选择账单对应的账户'),
  /** 用户显式指定的编码（缺省 = 自动嗅探，E1 的兜底路径） */
  encoding: ImportEncodingSchema.optional(),
  /** 源文件名（仅展示用，**不落库内容**） */
  fileName: z.string().trim().max(120).optional(),
});
export type ImportPreviewQuery = z.infer<typeof ImportPreviewQuerySchema>;

/** 置信度三档（`§5.3.1`） */
export type ConfidenceBand = 'high' | 'mid' | 'low';

/** 预览里的一行（**已带建议与去重判定**，端侧只渲染） */
export interface ImportPreviewRow {
  /** 原始网格行号（0 起）—— 确认时用它回指决策 */
  gridRow: number;
  date: string;
  timeText: string | null;
  /** 金额（分，恒正） */
  amountCents: Cents;
  direction: 'in' | 'out';
  merchant: string;
  normalizedMerchant: string;
  /** `expense` / `income` / `transfer` / `refund` */
  event: string;
  note: string | null;
  /** 建议分类（`null` = 未分类，**不是错误**） */
  suggestedCategoryId: string | null;
  /** 置信度（E4，0–10000） */
  confidenceE4: number;
  confidenceBand: ConfidenceBand;
  /** `rule` / `history` / `dictionary` / `none` */
  suggestSource: string;
  /** 与已存规则不一致时的另一条候选（`AC-D3.5`） */
  conflictCategoryId: string | null;
  /** 去重判定（`L1` = 将被跳过；`L2`/`L3` = 标注） */
  duplicateLevel: 'L1' | 'L2' | 'L3' | null;
  /** 🔴 `true` ⇒ 这一行**不会落库**（L1 精确重复，`AC-D2.1`） */
  willSkip: boolean;
}

/** 跳过的行（`E10` 等），**逐条带原因**（`AC-D5.2` 无静默失败） */
export interface ImportSkippedRowView {
  gridRow: number;
  reason: string;
}

/** 预览结果 */
export interface ImportPreviewResult {
  /** 落库时回指这份解析结果（内存 TTL，见文件头） */
  previewId: string;
  /** 实际使用的编码 */
  encoding: string;
  hasBom: boolean;
  fileName: string | null;
  accountId: string;
  currency: string;
  /** 识别到的列（列名回显，便于用户核对） */
  columns: {
    date: string;
    amount: string;
    merchant: string;
    direction: string | null;
  };
  rows: readonly ImportPreviewRow[];
  skipped: readonly ImportSkippedRowView[];
  counts: {
    /** 解析出的有效行数 */
    parsed: number;
    /** 被跳过的行数（含金额为 0 / 日期非法） */
    skipped: number;
    /** 因 L1 重复而不落库的行数 */
    willSkip: number;
    /** 各事件分布 */
    transfer: number;
    refund: number;
    /** L3 疑似重复（只标注、仍落库） */
    suspect: number;
  };
}

/**
 * `POST /api/imports` —— 确认落库（JSON）。
 *
 * 🔴 `rows` **只带决策**（`gridRow` + 分类 + 是否记住规则），金额/日期/方向一律以服务端的解析结果为准。
 */
export const ImportCommitRequestSchema = z.object({
  previewId: z.string().trim().min(1, '预览已过期，请重新上传'),
  /** 可覆盖预览时的账户（用户改主意） */
  accountId: z.string().trim().min(1).optional(),
  /** 逐行决策；**未出现在这里的行 = 用建议分类** */
  rows: z
    .array(
      z.object({
        gridRow: z.number().int().nonnegative(),
        /** `null` = 未分类 */
        categoryId: z.string().trim().min(1).nullable(),
        /** `true` = 把「归一商户 → 分类」写进 `import_rules`（`AC-D3.8`） */
        rememberRule: z.boolean().optional(),
      }),
    )
    .max(ImportPolicy.MaxRows)
    .optional(),
  /** 认领归属（`§5.4` 的「整批归因到 [成员]」）—— 缺省 = 导入人 */
  attributedToUserId: z.string().trim().min(1).optional(),
});
export type ImportCommitRequest = z.infer<typeof ImportCommitRequestSchema>;

/** 落库结果 */
export interface ImportCommitResultView {
  batchId: string;
  /** 实际落库笔数（**输入事实**，不是解析笔数） */
  imported: number;
  /** 因 L1 重复跳过的笔数 */
  skippedDuplicates: number;
  /** 因 L1 之外原因被解析器跳过的笔数（回显，便于对账） */
  skippedInvalid: number;
  /** 窗口内可撤销至（UTC ms） */
  revocableUntil: number | null;
  /** 学到的规则条数（`rememberRule` 生效数） */
  rulesLearned: number;
  /** 落库后仍**未分类**的笔数（H 域「查漏补记」的输入） */
  uncategorized: number;
}

/** 批次列表项（`AC-D2.9` 的可撤销窗口） */
export interface ImportBatchView {
  id: string;
  fileName: string | null;
  importedBy: string;
  importedByName: string;
  itemCount: number;
  status: 'committed' | 'revoked';
  createdAt: number;
  revocableUntil: number | null;
  /** 🔴 现在能不能撤销（服务端算好，端侧不判 30 天） */
  revocable: boolean;
  /** 该批次里**手工改过**的笔数（撤销时要单独确认，`AC-D2.10`） */
  editedCount: number;
}

export interface ImportBatchListResult {
  items: readonly ImportBatchView[];
  currency: string;
}

/** 撤销结果 */
export interface ImportRevokedView {
  batchId: string;
  /** 被软删的流水笔数 */
  revokedTxns: number;
  /** 沿用「删除」语义：30 天内可恢复 */
  restorable: boolean;
}

// ─────────────────────────── 规则管理（`AC-D3.6` / `D3.7`） ───────────────────────────

/** 规则视图（**只存「归一商户名 → 分类」，不存流水**） */
export interface ImportRuleView {
  id: string;
  normalizedMerchantName: string;
  categoryId: string;
  categoryName: string | null;
  hitCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface ImportRuleListResult {
  items: readonly ImportRuleView[];
}

/** `PATCH /api/import-rules/:id` —— 只允许改分类（商户名是键，改它等于新建一条） */
export const UpdateImportRuleRequestSchema = z.object({
  categoryId: z.string().trim().min(1, '请选择分类'),
});
export type UpdateImportRuleRequest = z.infer<typeof UpdateImportRuleRequestSchema>;

export interface ImportRuleDeletedView {
  id: string;
}

/**
 * `POST /api/imports/:id/revoke?force=` —— 撤销批次（`AC-D2.10`：手工改过的要单独确认）。
 *
 * 🔴 `force` 用**字面量 + transform**，不用 `z.coerce.boolean()` —— 后者会把 `'false'` 当 `true`
 * （批 56 实测踩过，`?includeEarlier=false` 静默变真）。
 */
export const ImportRevokeQuerySchema = z.object({
  force: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
export type ImportRevokeQuery = z.infer<typeof ImportRevokeQuerySchema>;
